import React, { useEffect, useRef, useState } from "react";
import SignaturePad from "signature_pad";
import { toast } from "sonner";

interface Props {
    onCapture: (base64: string, saveToProfile: boolean) => void;
    onCancel: () => void;
}

const SignatureCanvas: React.FC<Props> = ({ onCapture, onCancel }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const signaturePadRef = useRef<SignaturePad | null>(null);
    const [penColor, setPenColor] = useState("#000000");
    const [saveToProfile, setSaveToProfile] = useState(true);

    useEffect(() => {
        if (canvasRef.current) {
            signaturePadRef.current = new SignaturePad(canvasRef.current, {
                backgroundColor: "rgb(255, 255, 255)",
                penColor: penColor,
            });

            // Handle resize to ensure canvas isn't distorted
            const resizeCanvas = () => {
                const canvas = canvasRef.current;
                const wrapper = canvas?.parentElement;
                if (!canvas || !wrapper) return;

                const ratio = Math.max(window.devicePixelRatio || 1, 1);
                const w = wrapper.clientWidth;
                const h = wrapper.clientHeight;

                if (w === 0 || h === 0) return; // Wait for layout

                canvas.width = w * ratio;
                canvas.height = h * ratio;
                canvas.getContext("2d")?.scale(ratio, ratio);
                signaturePadRef.current?.clear();
            };

            window.addEventListener("resize", resizeCanvas);
            // Small delay to allow sidebar animation to finish
            const timer = setTimeout(resizeCanvas, 300);

            return () => {
                window.removeEventListener("resize", resizeCanvas);
                clearTimeout(timer);
                signaturePadRef.current?.off();
            };
        }
    }, []);

    useEffect(() => {
        if (signaturePadRef.current) {
            signaturePadRef.current.penColor = penColor;
        }
    }, [penColor]);

    const handleClear = () => {
        signaturePadRef.current?.clear();
    };

    const handleDone = () => {
        if (!signaturePadRef.current) {
            console.error("[SignatureCanvas] signaturePadRef is null");
            return;
        }

        const empty = signaturePadRef.current.isEmpty();

        if (empty) {
            toast.error("Por favor, dibuja tu firma antes de continuar.");
            return;
        }
        const dataUrl = signaturePadRef.current.toDataURL("image/png");
        if (dataUrl) {
            try {
                onCapture(dataUrl, saveToProfile);
                toast.success(saveToProfile ? "Firma capturada y guardada en perfil." : "Firma capturada e insertada.");
            } catch (err) {
                console.error("[SignatureCanvas] Error in onCapture:", err);
                toast.error("Error al insertar la firma.");
            }
        }
    };

    return (
        <div className="signature-canvas-container" style={styles.container}>
            <div style={styles.canvasWrapper}>
                <canvas ref={canvasRef} style={styles.canvas} />
            </div>

            <div style={styles.controls}>
                <div style={styles.colors}>
                    <button
                        onClick={() => setPenColor("#000000")}
                        style={{ ...styles.colorBtn, background: "#000", border: penColor === "#000000" ? "2px solid #6366f1" : "2px solid transparent" }}
                    />
                    <button
                        onClick={() => setPenColor("#063970")}
                        style={{ ...styles.colorBtn, background: "#063970", border: penColor === "#063970" ? "2px solid #6366f1" : "2px solid transparent" }}
                    />
                </div>

                <div style={styles.actions}>
                    <button onClick={handleClear} style={styles.secondaryBtn}>Limpiar</button>
                    <button onClick={onCancel} style={styles.secondaryBtn}>Cancelar</button>
                    <button onClick={handleDone} style={styles.primaryBtn}>Listo</button>
                </div>
            </div>

            <div style={styles.options}>
                <label style={styles.checkboxLabel}>
                    <input
                        type="checkbox"
                        checked={saveToProfile}
                        onChange={(e) => {
                            setSaveToProfile(e.target.checked);
                        }}
                        style={styles.checkbox}
                    />
                    <span style={{ fontWeight: 600, color: "#fff" }}>Guardar en mi repositorio personal</span>
                </label>
            </div>

            <p style={styles.hint}>Dibuja tu firma arriba</p>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        background: "transparent",
        padding: "0",
        borderRadius: "8px",
    },
    canvasWrapper: {
        background: "#fff",
        borderRadius: "6px",
        height: "360px",
        width: "100%",
        cursor: "crosshair",
        overflow: "hidden",
        border: "1px solid #475569",
    },
    canvas: {
        width: "100%",
        height: "100%",
        touchAction: "none",
    },
    controls: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    colors: {
        display: "flex",
        gap: "0.5rem",
    },
    colorBtn: {
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        cursor: "pointer",
        padding: 0,
    },
    actions: {
        display: "flex",
        gap: "0.4rem",
    },
    primaryBtn: {
        padding: "0.4rem 0.8rem",
        background: "#6366f1",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        fontSize: "0.75rem",
        fontWeight: 600,
        cursor: "pointer",
    },
    secondaryBtn: {
        padding: "0.4rem 0.8rem",
        background: "#334155",
        color: "#e2e8f0",
        border: "none",
        borderRadius: "6px",
        fontSize: "0.75rem",
        cursor: "pointer",
    },
    hint: {
        fontSize: "0.65rem",
        color: "#64748b",
        textAlign: "center",
        margin: 0,
        fontStyle: "italic",
    },
    options: {
        padding: "0.75rem",
        display: "flex",
        alignItems: "center",
        background: "rgba(99, 102, 241, 0.1)",
        borderRadius: "8px",
        margin: "0.5rem 0",
        border: "1px dashed rgba(99, 102, 241, 0.3)",
    },
    checkboxLabel: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        fontSize: "0.75rem",
        color: "#94a3b8",
        cursor: "pointer",
    },
    checkbox: {
        width: "14px",
        height: "14px",
        cursor: "pointer",
    }
};

export default SignatureCanvas;
